import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, MicOff, Pause, Play, PhoneOff, Send } from 'lucide-react';
import type { Maestro } from '@/types/maestro.ts';
import { getMaestroName, getMaestroTitle } from '@/types/maestro.ts';
import type { ConversationMessage } from '@/types/conversation.ts';
import { personaPhotoUrl } from '@/utils/personaPhoto.ts';

function initials(name: string): string {
  return (name || '').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

interface SessionFaceToFaceProps {
  maestro: Maestro;
  personaName: string | null;
  userName: string;
  userAvatarUrl?: string | null;
  messages: ConversationMessage[];
  isSpeaking: boolean;
  isMuted: boolean;
  isPaused: boolean;
  elapsedSeconds: number;
  /** Pitch flow: target duration in seconds (30/60/90); null for evaluate. */
  targetSeconds?: number | null;
  voiceEnabled: boolean;
  disabled?: boolean;
  onToggleMute: () => void;
  onTogglePause: () => void;
  onFinish: () => void;
  onSendTextMessage: (text: string) => void;
}

function fmt(s: number): string {
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export default function SessionFaceToFace({
  maestro, personaName, userName, userAvatarUrl, messages,
  isSpeaking, isMuted, isPaused, elapsedSeconds, targetSeconds, voiceEnabled, disabled,
  onToggleMute, onTogglePause, onFinish, onSendTextMessage,
}: SessionFaceToFaceProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');

  const aiName = personaName?.trim() || getMaestroName(maestro);
  const aiFirst = aiName.split(/\s+/)[0];
  const aiPhoto = personaPhotoUrl(personaName || getMaestroName(maestro));

  // Who has the floor: the AI when it's speaking, otherwise the user (their turn),
  // unless paused/muted. Drives the active ring + caption label.
  const aiActive = isSpeaking;
  const userActive = !isSpeaking && !isPaused && !isMuted;

  const last = messages[messages.length - 1];
  const captionWho = isSpeaking
    ? t('faceToFace.speaking', { name: aiFirst, defaultValue: `Sta parlando ${aiFirst}` })
    : isPaused
      ? t('faceToFace.paused', 'In pausa')
      : isMuted
        ? t('faceToFace.muted', 'Microfono disattivato')
        : t('faceToFace.listening', 'In ascolto…');
  const captionText = last?.text ?? '';

  const accent = maestro.accentColor;

  return (
    <div className="relative flex flex-1 min-h-0 flex-col overflow-hidden rounded-3xl border border-border bg-card">
      <style>{`
        @keyframes rplPulse { 0%{opacity:.5;transform:scale(1)} 100%{opacity:0;transform:scale(1.35)} }
        @keyframes rplBar { 50%{height:100%} }
        .rpl-pulse{animation:rplPulse 2.2s ease-out infinite}
        .rpl-eq i{display:block;width:4px;border-radius:3px;height:20%;background:currentColor;animation:rplBar 1s ease-in-out infinite}
        .rpl-eq i:nth-child(2){animation-delay:.15s}.rpl-eq i:nth-child(3){animation-delay:.3s}
        .rpl-eq i:nth-child(4){animation-delay:.45s}.rpl-eq i:nth-child(5){animation-delay:.2s}
      `}</style>

      {/* ambient gradient */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{ background: 'var(--gradient-agencium)' }}
      />

      {/* arena: two faces */}
      <div className="relative flex flex-1 min-h-0 items-center justify-center gap-[clamp(28px,8vw,120px)] px-6">
        {/* AI */}
        <Face
          active={aiActive}
          ringColor={accent}
          label={aiName}
          sub={`${getMaestroName(maestro)} — ${getMaestroTitle(maestro)}`}
          chip={t('faceToFace.buyerChip', 'Acquirente IA')}
          chipClass="bg-[color-mix(in_srgb,var(--secondary)_14%,transparent)] text-secondary"
          photo={<img src={aiPhoto} alt={aiName} className="h-full w-full object-cover" />}
        />

        {/* connector */}
        <div className="hidden flex-col items-center gap-2 sm:flex">
          <span className="text-[11px] font-bold tracking-widest text-muted-foreground/70">LIVE</span>
          <span className="h-20 w-px rounded bg-gradient-to-b from-[var(--accent-brand-cyan)] to-[var(--accent-brand-secondary)]" />
        </div>

        {/* USER */}
        <Face
          active={userActive}
          ringColor="var(--primary)"
          label={userName}
          sub={t('faceToFace.youRole', 'Venditore')}
          chip={t('faceToFace.youChip', 'Tu')}
          chipClass="bg-primary/10 text-primary"
          photo={userAvatarUrl
            ? <img src={userAvatarUrl} alt={userName} className="h-full w-full object-cover" />
            : (
              <div className="flex h-full w-full items-center justify-center text-white" style={{ background: 'var(--gradient-agencium)' }}>
                <span className="font-display text-[clamp(28px,5vw,56px)]">{initials(userName)}</span>
              </div>
            )}
        />
      </div>

      {/* single live caption (no chat) */}
      <div className="relative mx-auto min-h-[92px] max-w-2xl px-6 text-center">
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-primary">{captionWho}</p>
        <p className="font-display text-xl leading-snug text-foreground sm:text-2xl">{captionText}</p>
      </div>

      {/* controls */}
      <div className="relative flex items-center justify-center gap-2.5 px-6 pb-6 pt-2">
        {voiceEnabled ? (
          <div className="flex items-center gap-2.5 rounded-full border border-border bg-background/70 p-2.5 backdrop-blur-md">
            <button
              type="button"
              onClick={onToggleMute}
              disabled={disabled || isPaused}
              title={isMuted ? t('controls.unmute', 'Riattiva microfono') : t('controls.mute', 'Disattiva microfono')}
              className="flex size-12 items-center justify-center rounded-full bg-muted text-foreground transition hover:bg-accent disabled:opacity-50"
            >
              {isMuted ? <MicOff className="size-5" /> : <Mic className="size-5" />}
            </button>
            <button
              type="button"
              onClick={onTogglePause}
              disabled={disabled}
              title={isPaused ? t('controls.resume', 'Riprendi') : t('controls.pause', 'Pausa')}
              className="flex size-12 items-center justify-center rounded-full text-white transition disabled:opacity-50"
              style={{ background: 'var(--gradient-agencium)' }}
            >
              {isPaused ? <Play className="size-5" /> : <Pause className="size-5" />}
            </button>
            <span
              className="px-2 text-sm font-bold tabular-nums"
              style={{ color: targetSeconds && elapsedSeconds > targetSeconds ? 'var(--destructive)' : 'var(--foreground)' }}
              title={targetSeconds ? t('faceToFace.target', 'Tempo obiettivo') : undefined}
            >
              {fmt(elapsedSeconds)}{targetSeconds ? ` / ${fmt(targetSeconds)}` : ''}
            </span>
            <button
              type="button"
              onClick={onFinish}
              title={t('controls.finish', 'Termina')}
              className="flex h-12 items-center gap-2 rounded-full bg-destructive px-5 font-semibold text-white transition hover:opacity-90"
            >
              <PhoneOff className="size-5" />
              <span className="hidden sm:inline">{t('controls.finish', 'Termina')}</span>
            </button>
          </div>
        ) : (
          <div className="flex w-full max-w-2xl items-center gap-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && text.trim()) { onSendTextMessage(text.trim()); setText(''); }
              }}
              disabled={disabled}
              placeholder={t('faceToFace.typePlaceholder', 'Scrivi la tua battuta…')}
              className="h-12 flex-1 rounded-full border border-border bg-background px-5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              type="button"
              onClick={() => { if (text.trim()) { onSendTextMessage(text.trim()); setText(''); } }}
              className="flex size-12 items-center justify-center rounded-full text-white"
              style={{ background: 'var(--gradient-agencium)' }}
            >
              <Send className="size-5" />
            </button>
            <button
              type="button"
              onClick={onFinish}
              className="flex h-12 items-center gap-2 rounded-full bg-destructive px-5 font-semibold text-white"
            >
              <PhoneOff className="size-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface FaceProps {
  active: boolean;
  ringColor: string;
  label: string;
  sub: string;
  chip: string;
  chipClass: string;
  photo: ReactNode;
}

function Face({ active, ringColor, label, sub, chip, chipClass, photo }: FaceProps) {
  return (
    <div
      className="flex flex-col items-center gap-3 transition-transform duration-500"
      style={{ transform: active ? 'scale(1.04)' : 'scale(1)', opacity: active ? 1 : 0.66 }}
    >
      <div className="relative aspect-square w-[clamp(120px,16vw,210px)]">
        {active && (
          <>
            <span className="rpl-pulse absolute -inset-3 rounded-full border-2" style={{ borderColor: ringColor }} />
            <span
              className="absolute -inset-2 rounded-full"
              style={{ boxShadow: `0 0 0 3px ${ringColor}`, opacity: 0.5 }}
            />
          </>
        )}
        <div className="absolute inset-0 overflow-hidden rounded-full border-4 border-card shadow-xl">
          {photo}
        </div>
      </div>
      <div className="text-center">
        <h3 className="font-display text-lg text-foreground sm:text-xl">{label}</h3>
        <p className="text-xs text-muted-foreground">{sub}</p>
        <span className={`mt-1.5 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${chipClass}`}>
          {chip}
        </span>
        <div className="mt-1.5 flex h-5 items-end justify-center gap-1 text-primary" style={{ opacity: active ? 1 : 0 }}>
          <div className="rpl-eq flex h-full items-end gap-1"><i></i><i></i><i></i><i></i><i></i></div>
        </div>
      </div>
    </div>
  );
}
